import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge from "../../components/common/Badge/Badge";
import Button from "../../components/common/Button/Button";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../components/common/Table/Table";
import { getApiInstance } from "../../utils/api";
import "./FichaCentroProductor.scss";
import "../informes/cantidadesGeneradas/CantidadesGeneradas.scss";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import InputDropdown from "../../components/inputs/InputDropdown/InputDropdown_New";
import { useZone } from "../../context/ZoneContext";
import { CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { IoTrashOutline, IoLockOpenOutline, IoStopOutline, IoReloadOutline, IoClose, IoImageOutline } from "react-icons/io5";
import {
    addOrUpdateRecentItem,
    mapList,
    mappedDataToFormData,
} from "../../utils/utils";
import { formatDate } from "../../utils/utils";
import PieChartGraph from "../../components/common/Charts/PieChartGraph";
import { getConfig } from "../../utils/config";
import ScrollableBarChartGraph from "../../components/common/Charts/ScrollableBarChartGraph";
import { FiFileText, FiDownload, FiEdit } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import Modal from "../../components/common/Modal/Modal";
import ValidacionesHistorico from "../../components/common/ValidacionesHistorico/ValidacionesHistorico";
import ResiduoDetalleModal from "../../components/layout/CentrosProductores/ResiduoDetalleModal";
import Notification from "../../components/common/notification/notification";
import { useRef, useMemo } from "react";

import {
    formatKg,
    formatPeso,
    formatPesoPorTipo,
    formatPesoResiduo,
    pesoAKilogramos,
    pesoEnUnidadResiduo,
    unidadResiduo,
} from "../../utils/format";
import { formateadorMoneda } from "../../utils/moneda";
import { useMoneda } from "../../context/MonedaContext";

/* Lo que GetObra trae de cada línea de residuo para pintar la ficha y que el
   guardado no usa. En multipart un objeto viaja como "[object Object]", y el
   documento en base64 puede pasar del límite de 4 MB por campo del servidor. */
const CAMPOS_RESIDUO_SOLO_LECTURA = [
    "residuo",
    "transportista",
    "gestorFinal",
    "gestorIntermedio",
    "tipoTratamientoIntermedio",
    "tipoTratamientoFinal",
    "documentoTransporteBase64",
    "documentoFinalBase64",
];

const obtenerEstiloDeProgreso = (valor, maximo) => {
    if (maximo === 0) {
        return {
            "--progress-color": "#f44336",
        };
    }

    const porcentaje = (valor / maximo) * 100;
    let color;

    if (porcentaje < 40) {
        color = "#f44336";
    } else if (porcentaje < 75) {
        color = "#ff9800";
    } else {
        color = "#4caf50";
    }

    return {
        "--progress-color": color,
    };
};

/**
 * Agrupa los residuos de un centro por tipo de residuo y calcula lo que necesitan
 * la pestaña de cantidades (barras, tarta) y la tabla de agrupados.
 *
 * Estaba dentro de la carga inicial de la ficha, así que al validar o editar un
 * residuo la tabla de generados cambiaba pero los agrupados y las gráficas se
 * quedaban con los datos de la primera carga.
 */
const calcularDerivadosResiduos = (residuos, codObra) => {
    const residuosGrouped = [];
    residuos.forEach((currentItem) => {
        if (!currentItem?.residuo) return;

        /* La valorización (kg) la calcula el backend según el tratamiento final,
           igual que el informe de Toneladas Valorizadas: si el tratamiento es de
           eliminación vale 0 aunque Porcentaje2 sea 100, porque Porcentaje2 es el
           % que va a ese tratamiento, no el % valorizado. Viene vacía cuando la
           línea no tiene tratamiento final o porcentaje: eso es "sin dato". */
        const conDatoValorizacion = currentItem.valorizacion != null;
        const valorizacionKg = conDatoValorizacion
            ? Number(currentItem.valorizacion) || 0
            : 0;

        let group = residuosGrouped.find(
            (g) => g.codResiduo === currentItem.codResiduo
        );

        if (!group) {
            group = {
                codObra,
                codResiduo: currentItem.codResiduo,
                codTipoResiduo: currentItem.codTipoResiduo,
                residuo:
                    currentItem.residuo.codigo != null &&
                    currentItem.residuo.codigo !== "" &&
                    currentItem.residuo.codigo !== undefined
                        ? currentItem.residuo.codigo +
                          " - " +
                          currentItem.residuo.nombre
                        : currentItem.residuo.nombre,
                peso: 0,
                pesoConDatoValorizacion: 0,
                pesoSinDatoValorizacion: 0,
                valorizacion: 0,
                costeTotal: 0,
            };

            residuosGrouped.push(group);
        }

        group.peso += currentItem.peso;
        group.costeTotal += currentItem.costeTotal;

        if (conDatoValorizacion) {
            group.pesoConDatoValorizacion += currentItem.peso;
            group.valorizacion += valorizacionKg;
        } else {
            group.pesoSinDatoValorizacion += currentItem.peso;
        }
    });

    const datosBarChart = residuosGrouped
        .map((group) => {
            const valorizacion = group.valorizacion || 0;
            const pctValorizacion =
                group.peso > 0
                    ? (valorizacion / group.peso) * 100
                    : 0;

            return {
                name: group.residuo,
                "Cantidad generada": group.peso,
                "Valorización": valorizacion,
                pctValorizacion,
                /* Lo usa el formateador de la barra para decidir kg o tn. */
                peligroso: [1, 3, 5].includes(Number(group.codTipoResiduo)),
            };
        })
        .sort(
            (a, b) =>
                b["Cantidad generada"] +
                b["Valorización"] -
                (a["Cantidad generada"] + a["Valorización"])
        );

    const totalValorizado = residuosGrouped.reduce(
        (sum, group) => sum + (group.valorizacion || 0),
        0
    );
    const totalEliminado = residuos.reduce(
        (sum, item) => sum + (item.eliminacion || 0),
        0
    );
    const totalGenerado = residuosGrouped.reduce(
        (sum, group) => sum + (group.peso || 0),
        0
    );
    const granTotal = totalValorizado + totalEliminado;
    const porcVal =
        granTotal > 0 ? (totalValorizado / granTotal) * 100 : 0;
    const porcElim =
        granTotal > 0 ? (totalEliminado / granTotal) * 100 : 0;

    const datosProcesadosPie = [
        {
            name: "Valorización",
            value: totalValorizado,
        },
        {
            name: "Eliminación",
            value: totalEliminado,
        },
    ];

    return {
        residuosGrouped,
        datosBarChart,
        datosProcesadosPie,
        totalGenerado,
        totalValorizado,
        totalEliminado,
        porcVal,
        porcElim,
    };
};

export default function FichaCentroProductor({ user }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const [centro, setCentro] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [cumplimientoLegal, setCumplimientoLegal] = useState([]);
    const [residuos, setResiduos] = useState(null);
    const [residuosAgrupados, setResiduosAgrupados] = useState([]);
    const [residuosGenerados, setResiduosGenerados] = useState([]);
    const [tarifas, setTarifas] = useState([]);
    const [imagenes, setImagenes] = useState([]);
    const [activeTabDatos, setActiveTabDatos] = useState("informacion");
    const [activeTabResiduos, setActiveTabResiduos] = useState("cantidades");
    const [activeTabDocumentacion, setActiveTabDocumentacion] =
        useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [exportando, setExportando] = useState(false);
    const [error, setError] = useState(null);
    const [openDropdownRowId, setOpenDropdownRowId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [modalEdicionAbierto, setModalEdicionAbierto] = useState(false);
    const [formEdicionCentro, setFormEdicionCentro] = useState({});
    const { selectedZone } = useZone();
    const { moneda } = useMoneda();
    const [distribuidoras, setDistribuidoras] = useState([]);
    const [consumo, setConsumo] = useState([]);
    const [proyectos, setProyectos] = useState([]);
    const [transportistas, setTransportistas] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [combustibles, setCombustibles] = useState([]);
    const [gestores, setGestores] = useState([]);
    const [tiposTratamiento, setTiposTratamiento] = useState([]);
    const [residuosObraGroupped, setResiduosObraGroupped] = useState([]);
    const [datosProcesadosBar, setDatosProcesadosBar] = useState([]);
    const [datosProcesadosPie, setDatosProcesadosPie] = useState([]);
    const [totalCantidadGenerada, setTotalCantidadGenerada] = useState(0);
    const [totalValorizacion, setTotalValoracion] = useState(0);
    const [totalEliminacion, setTotalEliminacion] = useState(0);
    const [porcentajeValorizacion, setPorcentajeValorizacion] = useState(0);
    const [porcentajeEliminacion, setPorcentajeEliminacion] = useState(0);
    const [tiposDocumentosObligatorios, setTiposDocumentosObligatorios] =
        useState([]);
    const [tiposDocumentosNoObligatorios, setTiposDocumentosNoObligatorios] =
        useState([]);
    const [isModalBitacoraOpen, setIsModalBitacoraOpen] = useState(false);
    const [selectedMonthBitacora, setSelectedMonthBitacora] = useState(null);
    const [selectedBitacoraRow, setSelectedBitacoraRow] = useState(null);

    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [historicoValidacion, setHistoricoValidacion] = useState(null);
    const [tiposPorResiduo, setTiposPorResiduo] = useState({});
    const [residuoDetalle, setResiduoDetalle] = useState(null);

    /* Peligrosos en kilos, no peligrosos en toneladas: la unidad del formulario
       depende del residuo elegido, igual que en la aplicación antigua. El backend
       recibe y devuelve siempre kilos, así que se convierte al entrar y al salir
       del formulario. */
    const tipoDeResiduo = (codResiduo) => tiposPorResiduo[String(codResiduo)];

    const etiquetaPesoResiduo = (data) =>
        t("centerSheet.weightUnit", { unit: unidadResiduo(tipoDeResiduo(data?.codResiduo)) });

    /* SetResiduoObra y UpdateResiduoObra solo aceptan multipart, pero la tabla
       manda JSON cuando no se adjunta ningún documento: el servidor respondía 415
       y no se podía editar solo una cantidad. Por eso aquí se envía siempre
       FormData.

       Si sí hay documento, la tabla ya entrega un FormData. Antes el peso no se
       convertía en ese caso y los no peligrosos se guardaban en tn en vez de kg;
       ahora se pasa a kg en los dos casos. */
    const residuoFormAPayload = (payload) => {
        const entradas =
            payload instanceof FormData
                ? Array.from(payload.entries())
                : Object.entries(payload ?? {});

        const datos = Object.fromEntries(
            entradas.filter(
                ([clave, valor]) =>
                    !CAMPOS_RESIDUO_SOLO_LECTURA.includes(clave) &&
                    // Un fichero (Blob o File) sí se envía; cualquier otro objeto no.
                    !(
                        valor !== null &&
                        typeof valor === "object" &&
                        !(valor instanceof Blob)
                    )
            )
        );

        if (datos.peso != null && datos.peso !== "") {
            datos.peso = pesoAKilogramos(
                datos.peso,
                tipoDeResiduo(datos.codResiduo)
            );
        }

        return mappedDataToFormData(datos);
    };

    /* CosteTotal lo calcula el backend sumando los seis conceptos, y la
       valorización sale del tratamiento final: al editar una línea no basta con
       refrescar en local lo que se envió, hay que volver a pedir la obra. De paso
       se recalculan los agrupados y las gráficas, que derivan de esta lista. */
    const recargarResiduos = async () => {
        try {
            const obra = await fetchCentroProductor(id);

            if (!Array.isArray(obra?.residuos)) obra.residuos = [];

            setCentro(obra);
            setResiduosGenerados(obra.residuos);
        } catch (error) {
            console.error("No se pudieron recargar los residuos:", error);
        }
    };

    /* El abono de chatarra es un ingreso: la aplicación antigua rechazaba el
       formulario si venía en positivo, con este mismo mensaje. */
    const validaResiduoForm = (data) => {
        const abono = data?.abonoChatarra;

        if (abono !== "" && abono != null && Number(abono) > 0) {
            return t("centerSheet.scrapCreditNegative");
        }

        return null;
    };

    /* "Tipo de tratamiento" (Eliminación / Valorización) no es un campo del residuo:
       solo existe en el formulario para acotar el desplegable de "Descripción tipo
       tratamiento", que es el que se guarda en codTipoTratamiento. Al editar hay que
       deducirlo del tratamiento guardado, porque si el padre viene vacío
       calculateAssociatedOptions deja el hijo sin opciones y la descripción aparece
       en blanco aunque esté guardada.

       Las opciones de tiposTratamiento llevan el tipo en `fatherId` (lo pone mapList),
       así que basta buscar por el código. */
    const tipoDeTratamiento = (codTipoTratamiento) => {
        if (codTipoTratamiento == null) return null;

        const opcion = (tiposTratamiento ?? []).find(
            (o) => String(o.value ?? o.id) === String(codTipoTratamiento)
        );

        return opcion?.fatherId ?? null;
    };

    const residuoFilaAFormulario = (row) => ({
        ...row,
        peso: pesoEnUnidadResiduo(row.peso, row.codTipoResiduo),
        tipoTratamiento:
            row.tipoTratamientoIntermedio?.tipo ??
            tipoDeTratamiento(row.codTipoTratamiento),
        tipoTratamiento2:
            row.tipoTratamientoFinal?.tipo ??
            tipoDeTratamiento(row.codTipoTratamiento2),
    });

    const config = getConfig();
    const isMabe = config?.REACT_APP_IS_MABE === "true";

    const puedeGestionarCentro = Number(user?.codAcceso) === 1;

    /* Reglas de la aplicación antigua (Views/Obras/Details.cshtml):
       sobre un centro cerrado (2) o incorrecto (7) no se toca nada; validar es
       solo de administradores (perfiles 1 y 2) y el histórico lo ven además los
       intermediarios (perfil 4). */
    const centroBloqueado =
        centro?.codEstado === 2 || centro?.codEstado === 7;
    const puedeEditarResiduos = puedeGestionarCentro && !centroBloqueado;
    const puedeValidar =
        puedeEditarResiduos &&
        (Number(user?.role) === 1 || Number(user?.role) === 2);
    const puedeVerValidaciones = [1, 2, 4].includes(Number(user?.role));

    /* GetObra devuelve los costes ya convertidos a la moneda del selector, y con
       ella el codigo aplicado. Se usa ese y no el del selector directamente: si el
       backend normaliza a otra (moneda no valida), el simbolo seguiria cuadrando
       con las cifras. Mientras no ha respondido, la del selector. */
    const monedaCentro = centro?.moneda ?? moneda;
    const formateaCoste = useMemo(
        () => formateadorMoneda(monedaCentro, { maximumFractionDigits: 2 }),
        [monedaCentro]
    );

    const abrirHistoricoValidaciones = (codTipoValidacion) => (row) => {
        setHistoricoValidacion({
            codTipoValidacion,
            codClave:
                row.codResiduoObra ?? row.codDocumento ?? row.CodClave ?? null,
        });
        setIsHistoricoOpen(true);
    };

    const notificationRef = useRef(null);
    const consumoTableRef = useRef(null);

    const formElementsDocumentos = [
        {
            elements: [
                {
                    name: "codTipoDocumento",
                    type: "radio",
                    label: t("centerSheet.documentType"),
                    placeholder: t("centerSheet.selectTheDocumentType"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: [
                        {
                            label: t("centerSheet.mandatoryDocumentation"),
                            options: tiposDocumentosObligatorios,
                        },
                        {
                            label: t("centerSheet.nonMandatoryDocumentation"),
                            options: tiposDocumentosNoObligatorios,
                        },
                    ],
                },
                {
                    name: "fichero",
                    type: "document",
                    label: t("centerSheet.document"),
                    placeholder: t("centerSheet.uploadDocument"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "observaciones",
                    type: "textarea",
                    label: t("centerSheet.observations"),
                    placeholder: t("centerSheet.writeTheObservationsHere"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codObra",
                    type: "text",
                    label: t("centerSheet.workCode"),
                    placeholder: "",
                    required: false,
                    disabled: true,
                    labelVisible: false,
                    visible: false,
                    defaultValue: id,
                },
            ],
        },
    ];

    useEffect(() => {
        if (centro != null) {
            addOrUpdateRecentItem({
                id: centro.codObra,
                title: centro.nombre,
                type: "centroProductor",
            });
        }
    }, [centro]);

    /* Los agrupados y las gráficas son datos derivados de los residuos generados:
       se recalculan solos cuando la tabla cambia (al añadir, editar, borrar o
       validar un residuo), sin volver a pedir la ficha entera. */
    useEffect(() => {
        const derivados = calcularDerivadosResiduos(residuosGenerados, id);

        setResiduosObraGroupped(derivados.residuosGrouped);
        setDatosProcesadosBar(derivados.datosBarChart);
        setDatosProcesadosPie(derivados.datosProcesadosPie);
        setTotalCantidadGenerada(derivados.totalGenerado);
        setTotalValoracion(derivados.totalValorizado);
        setTotalEliminacion(derivados.totalEliminado);
        setPorcentajeValorizacion(derivados.porcVal);
        setPorcentajeEliminacion(derivados.porcElim);
    }, [residuosGenerados, id]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                const obra = await fetchCentroProductor(id);

                if (!Array.isArray(obra?.residuos)) {
                    obra.residuos = [];
                }

                if (isMounted) {
                    setCentro(obra);
                    setResiduosGenerados(obra.residuos);
                }


                const results = await Promise.allSettled([
                    fetchDocumentosCentroProductor(id),
                    fetchElectricas(),
                    fetchConsumo(id),
                    fetchResiduos(),
                    fetchProyectos(),
                    fetchResiduosTiposTratamiento(),
                    fetchTiposVehiculos(),
                    fetchTiposCombustible(),
                    fetchImagenes(id),
                    fetchTarifas(id),
                    fetchTiposDocumentos(),
                    fetchProveedores(),
                ]);

                const [
                    documentosResult,
                    electricasResult,
                    consumoResult,
                    residuosResult,
                    proyectosResult,
                    tiposTratamientoResult,
                    vehiculosResult,
                    combustiblesResult,
                    imagenesResult,
                    tarifasResult,
                    tiposDocumentosResult,
                    proveedoresResult,
                ] = results;

                if (isMounted) {
                    if (documentosResult.status === "fulfilled") {
                        setDocumentos(documentosResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar los documentos:",
                            documentosResult.reason
                        );
                    }

                    if (electricasResult.status === "fulfilled") {
                        setDistribuidoras(
                            mapList(
                                electricasResult.value,
                                "nombre",
                                "codElectrica"
                            )
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            electricasResult.reason
                        );
                    }

                    if (consumoResult.status === "fulfilled") {
                        setConsumo(consumoResult.value);
                    } else {
                        console.warn(
                            "No se pudo cargar el consumo:",
                            consumoResult.reason
                        );
                    }

                    if (residuosResult.status === "fulfilled") {
                        /* El tipo de cada residuo decide la unidad del peso. El mapa
                           se construye con TODOS los residuos, no solo los de la
                           zona: si una línea ya guardada usa un residuo de otra
                           zona, al editarla hay que seguir sabiendo su unidad. */
                        setTiposPorResiduo(
                            Object.fromEntries(
                                residuosResult.value.map((r) => [
                                    String(r.codResiduo),
                                    r.codTipoResiduo,
                                ])
                            )
                        );

                        /* Solo los residuos de la zona del centro productor, como
                           la aplicación antigua, que cargaba la lista con
                           GetResiduosByZona(CodZona). */
                        const residuosElegibles = residuosResult.value.filter(
                            (r) => Number(r.codZona) === Number(obra.codZona)
                        );

                        setResiduos(
                            mapList(residuosElegibles, "nombre", "codResiduo").map(item => {
                                const residuo = residuosElegibles.find(
                                    r => r.codResiduo === item.id
                                );

                                const nombre =
                                    residuo?.codigo && residuo.codigo !== "-"
                                        ? `${residuo.codigo} - ${item.label}`
                                        : item.label;

                                return {
                                    ...item,
                                    /* El asterisco marca los peligrosos, como en
                                       el selector de la aplicación antigua. */
                                    label:
                                        unidadResiduo(
                                            residuo?.codTipoResiduo
                                        ) === "kg"
                                            ? `* ${nombre}`
                                            : nombre,
                                };
                            })
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar los residuos:",
                            residuosResult.reason
                        );
                    }

                    if (proyectosResult.status === "fulfilled") {
                        setProyectos([
                            ...mapList(
                                proyectosResult.value,
                                "proyecto",
                                "codProyecto"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los proyectos:",
                            proyectosResult.reason
                        );
                    }

                    if (proveedoresResult.status === "fulfilled") {
                        setTransportistas([
                            ...mapList(
                                proveedoresResult.value.filter(
                                    (x) => x.isTransportista && x.codEstado == 3 && x.codPais === obra.codPais
                                ),
                                "nombre",
                                "codProveedor",
                                "codResiduo",
                                "codsResiduos"
                            ),
                        ]);
                        setGestores([
                            ...mapList(
                                proveedoresResult.value.filter(
                                    (x) => x.isGestorFinal && x.codEstado == 3 && x.codPais === obra.codPais
                                ),
                                "nombre",
                                "codProveedor",
                                "codResiduo",
                                "codsResiduos"
                            ),
                        ]);
                    }

                    if (tiposTratamientoResult.status === "fulfilled") {
                        setTiposTratamiento([
                            ...mapList(
                                tiposTratamientoResult.value,
                                ["codigo", "descripcion"],
                                "codTipoTratamiento",
                                "codTipoTratamiento",
                                "tipo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de tratamiento:",
                            tiposTratamientoResult.reason
                        );
                    }

                    if (vehiculosResult.status === "fulfilled") {
                        setVehiculos([
                            ...mapList(
                                vehiculosResult.value,
                                "vehiculo",
                                "codVehiculo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de vehículos:",
                            vehiculosResult.reason
                        );
                    }

                    if (combustiblesResult.status === "fulfilled") {
                        setCombustibles([
                            ...mapList(
                                combustiblesResult.value,
                                "combustible",
                                "codCombustible",
                                "codVehiculo",
                                "codsVehiculo"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos de combustible:",
                            combustiblesResult.reason
                        );
                    }

                    if (imagenesResult.status === "fulfilled") {
                        setImagenes(imagenesResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las imágenes:",
                            imagenesResult.reason
                        );
                    }

                    if (tarifasResult.status === "fulfilled") {
                        setTarifas(tarifasResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las imágenes:",
                            tarifasResult.reason
                        );
                    }

                    if (tiposDocumentosResult.status === "fulfilled") {
                        const tiposDocObligatorios =
                            tiposDocumentosResult.value.filter(
                                (doc) => doc.obligatorio
                            );
                        const tiposDocNoObligatorios =
                            tiposDocumentosResult.value.filter(
                                (doc) => !doc.obligatorio
                            );
                        setTiposDocumentosObligatorios([
                            ...mapList(
                                tiposDocObligatorios,
                                "descripcion",
                                "codTipoDocumento"
                            ),
                        ]);
                        setTiposDocumentosNoObligatorios([
                            ...mapList(
                                tiposDocNoObligatorios,
                                "descripcion",
                                "codTipoDocumento"
                            ),
                        ]);
                        console.log(tiposDocObligatorios);
                        console.log(tiposDocNoObligatorios);
                    } else {
                        console.warn(
                            "No se pudieron cargar los tipos documento:",
                            tiposDocumentosResult.reason
                        );
                    }
                }
            } catch (err) {
                if (isMounted) {
                    /* 403: el backend no deja abrir centros que no son del usuario
                       (por ejemplo, cambiando el id en la URL). */
                    setError(
                        err?.response?.status === 403
                            ? "No tienes acceso a este centro productor."
                            : t("centerSheet.errorLoadingMain")
                    );
                }
                console.error("Error en datos críticos de loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [id, selectedZone, refreshKey, moneda]);

    const fetchCentroProductor = async (id) => {
        try {
            const api = getApiInstance();
            /* La moneda del selector de la cabecera: el backend devuelve los costes
               ya convertidos a ella, con el simbolo aplicado. */
            const response = await api.get(
                `/api/Obra/GetObra/${id}/es`,
                { params: moneda ? { moneda } : undefined }
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentosCentroProductor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraDocumento/GetDocumentosFromObra/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchConsumo = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Consumo/GetConsumoByCodObra/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchElectricas = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Electrica/GetAllElectricas`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchResiduos = async () => {
        const api = getApiInstance();
        try {
            const response = await api.get(
                `/api/Residuos/GetAllResiduos`
            );
            console.log("Residuos fetched:", response.data);
            return await response.data;
        } catch (err) {
            console.error(err);
            throw error;
        }
    };

    const fetchProyectos = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Proyecto/GetProyectos`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching proyectos:", error);
            throw error;
        }
    };

    const fetchProveedores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Proveedores/GetProveedores`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching proyectos:", error);
            throw error;
        }
    };

    const fetchResiduosTiposTratamiento = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ResiduosTipoTratamiento/GetResiduosTipoTratamiento/es`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos tratamiento:", error);
            throw error;
        }
    };

    const fetchTiposVehiculos = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Huella/GetTiposVehiculo`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos vehiculos:", error);
            throw error;
        }
    };

    const fetchTiposCombustible = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Huella/GetTiposCombustible`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos combustible:", error);
            throw error;
        }
    };

    const fetchImagenes = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/Obra/GetImagenesObra/${id}`);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTarifas = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Tarifa/GetTarifasAsignadasByObra/${id}`
            );

            console.log("Tarifas fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTarifasProveedores = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Tarifa/GetTarifasAsignadasByObra/${id}`
            );

            console.log("Tarifas fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching imágenes:", error);
            throw error;
        }
    };

    const fetchTiposDocumentos = async () => {
        try {
            // Productor = 1,
            // Gestor = 2,
            // Obra = 3,
            const api = getApiInstance();
            const response = await api.get(
                `/api/Documentos/GetTiposDocumentosByTipoEntidad/3`
            );

            console.log("Tipos Documentos fetched:", response.data);

            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos documentos:", error);
            throw error;
        }
    };

    const handleTarifaChange = (id, nuevaTarifa) => {
        setTarifas((prevTarifas) =>
            prevTarifas.map((tarifa) =>
                tarifa.id === id ? { ...tarifa, tarifa: nuevaTarifa } : tarifa
            )
        );
    };

    const handleDropdownOpenChange = (rowId, isOpen) => {
        setOpenDropdownRowId(isOpen ? rowId : null);
    };

    const tabsDatos = [
        { id: "informacion", label: t("centerSheet.generalInformation") },
        { id: "ubicacion", label: t("centerSheet.location") },
    ];

    const tabsResiduos = [
        { id: "cantidades", label: t("centerSheet.generatedQuantities") },
        { id: "agrupados", label: t("centerSheet.groupedGeneratedWaste") },
        { id: "generados", label: t("centerSheet.generatedWaste") },
        { id: "tarifas", label: t("centerSheet.rates") },
    ];

    const tabsDocumentacion = [
        { id: "documentacion", label: t("centerSheet.documentation") },
        { id: "cumplimiento", label: t("centerSheet.legalCompliance") },
        { id: "imagenes", label: t("centerSheet.images") },
    ];

    const renderInformacionGeneral = () => (
        <div className="ficha-centro__info">
            <div className="ficha-centro__field">
                <label>{t("centerSheet.spaceCodeColon")}</label>
                <span>{centro.codEspacio || "'"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.workCodeColon")}</label>
                <span>{centro.nombre || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.workTypeColon")}</label>
                <span>{centro.tipoObra || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>NIMA:</label>
                <span>{centro.nima || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.chainColon")}</label>
                <span>{centro.cadena || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.startDateColon")}</label>
                <span>{formatDate(centro.fechaInicio) || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.endDateColon")}</label>
                <span>{formatDate(centro.fechaFin) || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.intermediaryColon")}</label>
                <span>{centro.intermediario || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.responsibleColon")}</label>
                <span>{centro.responsable || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.surfaceColon")}</label>
                <span>{centro.superficie || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.observationsColon")}</label>
                <span>{centro.observaciones || "-"}</span>
            </div>
        </div>
    );

    const renderUbicacion = () => (
        <div className="ficha-centro__info">
            <div className="ficha-centro__field">
                <label>{t("centerSheet.locationColon")}</label>
                <span>{centro.ubicacion || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.addressColon")}</label>
                <span>{centro.direccion || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.localityColon")}</label>
                <span>{centro.localidad || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.provinceColon")}</label>
                <span>{centro.provincia?.provincia || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.communityColon")}</label>
                <span>{centro.comunidad || "-"}</span>
            </div>
            <div className="ficha-centro__field">
                <label>{t("centerSheet.countryColon")}</label>
                <span>{centro.pais || "-"}</span>
            </div>
        </div>
    );

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "informacion":
                return renderInformacionGeneral();
            case "ubicacion":
                return renderUbicacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderCantidadesGeneradas = () => (
        <div className="cantidades-generadas">
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    alignItems: "start",
                }}
            >
                <ScrollableBarChartGraph
                    data={datosProcesadosBar}
                    passItemToFormatter
                    valueFormatter={(value, item) =>
                        formatPesoPorTipo(value, Boolean(item?.peligroso))
                    }
                />

                <PieChartGraph
                    data={{
                        all: datosProcesadosPie.map((item, i) => ({
                            ...item,
                            name: t(
                                i === 0
                                    ? "ui.charts.valorization"
                                    : "ui.charts.elimination"
                            ),
                        })),
                    }}
                    activeTab="all"
                    tooltipAsPercent={false}
                    tooltipValueFormatter={(value) =>
                        `${formatPeso(value)}`
                    }
                    legendAsPercent
                    centerLabel={`${Number(porcentajeValorizacion).toFixed(
                        1
                    )}%`}
                    centerSubLabel={t("centerSheet.valorization")}
                />
            </div>
        </div>
    );

    const renderImagenes = () => {
        const handleEliminarImagen = async (imagenId, fileName, codObra) => {
            console.log("Eliminar imagen:", imagenId, fileName, codObra);

            if (
                window.confirm(
                    t("centerSheet.areYouSureYouWantToDelete")
                )
            ) {
                try {
                    const api = getApiInstance();
                    await api.delete(
                        `/api/Obra/DeleteImagen/${imagenId}/${codObra}/${fileName}`
                    );
                    setImagenes(
                        imagenes.filter((img) => img.codImagen !== imagenId)
                    );
                } catch (error) {
                    console.error("Error eliminando imagen:", error);
                    alert(t("centerSheet.errorDeletingTheImagePleaseTryAgain"));
                }
            }
        };

        const handleSubirImagenes = async (e) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            try {
                const api = getApiInstance();
                const formData = new FormData();

                for (const file of files) {
                    formData.append("Imagenes", file);
                }

                const { data } = await api.post(
                    `/api/Obra/SetImage/${id}`,
                    formData
                );

                const imagenesActualizadas = await fetchImagenes(id);
                setImagenes(imagenesActualizadas);
            } catch (error) {
                console.error("Error subiendo imágenes:", error);
                alert(t("centerSheet.errorUploadingTheImagesPleaseTryAgain"));
            } finally {
                // Resetea el input para poder volver a seleccionar el mismo archivo si se desea
                e.target.value = "";
            }
        };

        const formatearFecha = (fechaString) => {
            const fecha = new Date(fechaString);
            const dia = String(fecha.getDate()).padStart(2, "0");
            const mes = String(fecha.getMonth() + 1).padStart(2, "0");
            const año = fecha.getFullYear();
            const horas = String(fecha.getHours()).padStart(2, "0");
            const minutos = String(fecha.getMinutes()).padStart(2, "0");
            const segundos = String(fecha.getSeconds()).padStart(2, "0");
            return `${dia}/${mes}/${año} ${horas}:${minutos}:${segundos}`;
        };

        if (!imagenes || imagenes.length === 0) {
            return (
                <div className="imagenes-section">
                    <div className="imagenes-upload-area">
                        <div className="imagenes-upload-content">
                            <p>{t("centerSheet.clickHereToUploadImages")}</p>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleSubirImagenes}
                                style={{ display: "none" }}
                                id="upload-imagenes"
                            />
                            <label
                                htmlFor="upload-imagenes"
                                className="upload-label"
                            >
                                {t("centerSheet.selectFiles")}
                            </label>
                        </div>
                    </div>
                    <div className="imagenes-empty">
                        <p>{t("centerSheet.thereAreCurrentlyNoImagesToDisplay")}</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="imagenes-section">
                <div className="imagenes-upload-area">
                    <div className="imagenes-upload-content">
                        <p>{t("centerSheet.clickHereToUploadImages")}</p>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleSubirImagenes}
                            style={{ display: "none" }}
                            id="upload-imagenes"
                        />
                        <label
                            htmlFor="upload-imagenes"
                            className="upload-label"
                        >
                            {t("centerSheet.selectFiles")}
                        </label>
                    </div>
                </div>
                <div className="imagenes-grid">
                    {imagenes.map((imagen) => (
                        <div key={imagen.codImagen} className="imagen-card">
                            <div className="imagen-thumbnail">
                                {/* GetArchivoBase64 devuelve "" cuando no encuentra
                                    el fichero en wwwroot y se traga la excepción, así
                                    que la fila existe en base pero la imagen no. Sin
                                    esto se pintaba un <img> roto con el texto alt. */}
                                {imagen.fileBase64 ? (
                                    <img
                                        src={`data:image/png;base64,${imagen.fileBase64}`}
                                        alt={t("centerSheet.imageN", { id: imagen.codImagen })}
                                    />
                                ) : (
                                    <div className="imagen-thumbnail__vacia">
                                        <IoImageOutline />
                                        <span>{t("centerSheet.imageNotAvailable")}</span>
                                    </div>
                                )}
                            </div>
                            <div className="imagen-info">
                                <div className="imagen-detalles">
                                    <p className="imagen-usuario">
                                        {imagen.usuario || "Usuario"}
                                    </p>
                                    <p className="imagen-fecha">
                                        {formatearFecha(imagen.fechaSubida)}
                                    </p>
                                </div>
                                <button
                                    className="eliminar-imagen-btn"
                                    onClick={() =>
                                        handleEliminarImagen(
                                            imagen.codImagen,
                                            imagen.fileName,
                                            id
                                        )
                                    }
                                    aria-label={t("centerSheet.deleteImage")}
                                    title={t("centerSheet.deleteImage")}
                                >
                                    <IoTrashOutline />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderDocumentacionContent = () => {
        switch (activeTabDocumentacion) {
            case "documentacion":
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: t("centerSheet.documentType"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: t("centerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: t("centerSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                    titleProp: "lastValidationNote",
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage={t("centerSheet.noDocumentsAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            /* Mismo gating que la app antigua para el boton de
                               nuevo documento: escritura y centro no cerrado. */
                            hideAddButton={!puedeEditarResiduos}
                            codTipoValidacion={6}
                            actions={[
                                ...(puedeValidar
                                    ? [
                                          {
                                              label: t("centerSheet.validate"),
                                              actionType: "validate",
                                              icon: <CiCircleCheck size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeVerValidaciones
                                    ? [
                                          {
                                              label: t("centerSheet.viewValidations"),
                                              action: abrirHistoricoValidaciones(
                                                  6
                                              ),
                                              icon: <CiClock1 size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeEditarResiduos ? ["Eliminar"] : []),
                            ]}
                            elementName={t("elements.document")}
                            keyProp="codDocumento"
                            searchTerms={["tipoDocumento", "documento"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: t("centerSheet.noData"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.pendingValidation"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.validationWithIncidents"),
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: t("centerSheet.validated"),
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                                {
                                    label: t("centerSheet.expired"),
                                    type: "danger",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                { value: "1", label: t("centerSheet.pendingValidation") },
                                {
                                    value: "2",
                                    label: t("centerSheet.validationWithIncidents"),
                                },
                                { value: "3", label: t("centerSheet.validated") },
                                { value: "4", label: t("centerSheet.expired") },
                            ]}
                            /* Sin permiso para validar no se pasa la URL: así la tabla no
                               ofrece el cambio de estado ni el botón de validar en bloque. */
                            stateChangeUrl={
                                puedeValidar
                                    ? "/api/Validaciones/SetValidacion"
                                    : undefined
                            }
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/ObraDocumento/SetDocumento"
                            deleteApiUrl="/api/ObraDocumento/DeleteDocumento"
                            onDataUpdate={(newData) => setDocumentos(newData)}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario sube y borra documentos de su
                               obra, como en la antigua. */
                            intermediarioPuedeEscribir
                            loading={loading}
                        />
                    </div>
                );
            case "cumplimiento":
                return (
                    <div className="ficha-centro__cumplimiento">
                        <Table
                            columns={[
                                {
                                    key: "nombre",
                                    label: t("centerSheet.description"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: t("centerSheet.status"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={centro.cumplimientos}
                            noResultsMessage={t("centerSheet.noLegalComplianceDataAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            elementName={t("elements.compliance")}
                            searchTerms={["nombre"]}
                            states={[
                                {
                                    label: t("centerSheet.noData"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.pending"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.notValidated"),
                                    type: "warning",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.validated2"),
                                    type: "success",
                                    Icon: CiClock1,
                                },
                            ]}
                            stateProp="codEstado"
                            sortProp="descripcion"
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={loading}
                        />
                    </div>
                );
            case "imagenes":
                return renderImagenes();
            default:
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: t("centerSheet.documentType"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: t("centerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "codEstado",
                                    label: t("centerSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage={t("centerSheet.noDocumentsAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Editar", "Eliminar"]}
                            elementName={t("elements.document")}
                            searchTerms={["tipoDocumento", "documento"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: t("centerSheet.noData"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.pendingValidation"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("centerSheet.validationWithIncidents"),
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: t("centerSheet.validated"),
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/gestores/documentos"
                            editApiUrl="/api/gestores/documentos"
                            deleteApiUrl="/api/gestores/documentos"
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            loading={false}
                        />
                    </div>
                );
        }
    };

    const renderConsumo = () => (
        <Table
            columns={[
                {
                    key: "electrica",
                    label: t("centerSheet.electricityCompany"),
                    visible: true,
                    sortable: true,
                },
                {
                    key: "fechaInicio",
                    label: t("centerSheet.startDate"),
                    visible: true,
                    sortable: true,
                    type: "date",
                },
                {
                    key: "fechaFin",
                    label: t("centerSheet.endDate"),
                    visible: true,
                    sortable: true,
                    type: "date",
                },
                {
                    key: "consumo",
                    label: t("centerSheet.consumption"),
                    visible: true,
                    sortable: true,
                },
                {
                    key: "factura",
                    label: t("centerSheet.invoice"),
                    visible: true,
                    sortable: true,
                    type: "document",
                    documentProp: "ficheroBase64",
                },
            ]}
            dataTable={consumo}
            noResultsMessage={t("centerSheet.noConsumptionDataAvailable")}
            rowsPerPage={10}
            showCheckboxes={false}
            elementName={t("elements.consumption")}
            sortProp="electrica"
            keyProp="codConsumo"
            searchTerms={["electrica", "fechaInicio", "fechaFin", "consumo"]}
            showAdvancedFilters={false}
            actions={["Eliminar", "Editar"]}
            loading={loading}
            formElements={[
                {
                    elements: [
                        {
                            name: "codObra",
                            type: "text",
                            required: false,
                            disabled: false,
                            visible: false,
                            defaultValue: `${id}`,
                        },
                        {
                            name: "codElectrica",
                            type: "select",
                            label: t("centerSheet.distributor"),
                            placeholder: t("centerSheet.selectADistributor"),
                            options: distribuidoras,
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "codGarantia",
                            type: "select",
                            label: t("centerSheet.guaranteeOfOriginGoo"),
                            placeholder:
                                t("centerSheet.doYouHaveAGuaranteeOfOrigin"),
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                            options: [
                                { value: "1", label: t("centerSheet.gooRenewableEnergy") },
                                {
                                    value: "2",
                                    label: t("centerSheet.gooHighEfficiencyCogeneration"),
                                },
                                { value: "3", label: t("common.no") },
                            ],
                        },
                        {
                            name: "fechaInicio",
                            type: "date",
                            label: t("centerSheet.startDate2"),
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "fechaFin",
                            type: "date",
                            label: t("centerSheet.endDate2"),
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "consumo",
                            type: "number",
                            label: t("centerSheet.consumptionKwh"),
                            placeholder: t("centerSheet.enterTheConsumptionInKwh"),
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                        {
                            name: "FicheroFactura",
                            type: "document",
                            label: t("centerSheet.invoice"),
                            placeholder: t("centerSheet.uploadInvoice"),
                            required: true,
                            disabled: false,
                            labelVisible: true,
                            visible: true,
                        },
                    ],
                },
            ]}
            addApiUrl="/api/Consumo/SetConsumo"
            editApiUrl="/api/Consumo/EditConsumo"
            deleteApiUrl="/api/Consumo/DeleteConsumo"
            userCodAcceso={Number(user.codAcceso)}
            userCodPerfil={Number(user.role)}
            hideAddButton
            ref={consumoTableRef}
        />
    );

    async function handleBitacora() {
        const api = getApiInstance();
        const response = await api.post(
            `api/Obra/obtener-bitacora-mabe?codObra=${id}`,
            null,

            {
                responseType: "blob",
            }
        );

        if (response.status !== 200) {
            throw new Error(t("centerSheet.errorGeneratingTheLog"));
        }
        const cd = response.headers["content-disposition"] || "";
        let fileName = `Informe_${id}.xlsx`;

        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);

        const normalMatch = cd.match(/filename="?([^"]+)"?/i);

        const rawFileName = utf8Match?.[1] || normalMatch?.[1];

        if (rawFileName) {
            try {
                fileName = decodeURIComponent(rawFileName.trim());
            } catch {
                fileName = rawFileName.trim();
            }
        }
        // Blob
        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type:
                          response.headers["content-type"] ||
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(urlBlob);

        return { ok: true, fileName, size: blob.size };
    }

    async function handleBitacoraResiduo(row) {
        const api = getApiInstance();

        console.log("Generando bitácora para residuo:", row);

        const response = await api.get("api/ResiduosObra/bitacora-residuos", {
            params: {
                codObra: id,
                codResiduo:
                    selectedBitacoraRow.codResiduo ??
                    selectedBitacoraRow.CodResiduo ??
                    selectedBitacoraRow.id,
                mes: selectedMonthBitacora.value,
                codZona: selectedZone.value,
            },
            responseType: "blob",
        });

        if (response.status !== 200) {
            setIsModalBitacoraOpen(false);
            setSelectedMonthBitacora(null);
            notificationRef.current?.addNotification(
                t("centerSheet.errorGeneratingTheLogPleaseTryAgain"),
                "error"
            );
        }

        const cd = response.headers["content-disposition"] || "";
        let fileName = `Manifiesto_MABE_${id}.docx`;

        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const normalMatch = cd.match(/filename="?([^"]+)"?/i);

        const rawFileName = utf8Match?.[1] || normalMatch?.[1];

        if (rawFileName) {
            try {
                fileName = decodeURIComponent(rawFileName.trim());
            } catch {
                fileName = rawFileName.trim();
            }
        }

        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type:
                          response.headers["content-type"] ||
                          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  });

        const urlBlob = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(urlBlob);

        setIsModalBitacoraOpen(false);
        setSelectedMonthBitacora(null);
        notificationRef.current?.addNotification(
            t("centerSheet.reportGeneratedSuccessfully"),
            "success"
        );

        return {
            ok: true,
            fileName,
            size: blob.size,
        };
    }

    const handleExportarObra = async () => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            /* El informe de la obra (el de la app antigua), no el listado de
               centros. Con la moneda del selector, como los costes de la ficha. */
            const response = await api.post(
                `/api/Obra/CreateReportExcel/${id}`,
                null,
                {
                    responseType: "blob",
                    params: moneda ? { moneda } : undefined,
                }
            );

            const cd = response.headers["content-disposition"] || "";
            let fileName = `Informe_CentroProductor_${id}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
            const normalMatch = cd.match(/filename="?([^"]+)"?/i);
            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            notificationRef.current?.addNotification(
                t("centerSheet.reportGeneratedSuccessfully"),
                "success"
            );
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                t("centerSheet.errorGeneratingTheReportPleaseTryAgain"),
                "error"
            );
        } finally {
            setExportando(false);
        }
    };

    const ejecutarAccionObra = async (endpoint, mensaje, exito) => {
        if (!centro) return;
        if (!window.confirm(mensaje)) return;

        try {
            const api = getApiInstance();
            await api.post(`${endpoint}/${id}`);
            notificationRef.current?.addNotification(exito, "success");
            setRefreshKey((k) => k + 1);
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                t("centerSheet.theActionCouldNotBeCompletedPlease"),
                "error"
            );
        }
    };

    const handleAbrirCentro = () => {
        ejecutarAccionObra(
            "/api/Obra/OpenObra",
            t("centerSheet.confirmReopen", { code: centro?.codEspacio }),
            t("centerSheet.centerOpened")
        );
    };

    const handleCerrarCentro = () => {
        const esIntermediario = Number(user.role) === 4;
        ejecutarAccionObra(
            esIntermediario
                ? "/api/Obra/RequestCloseObra"
                : "/api/Obra/CloseObra",
            esIntermediario
                ? t("centerSheet.confirmRequestClose", { code: centro?.codEspacio })
                : t("centerSheet.confirmClose", { code: centro?.codEspacio }),
            esIntermediario
                ? t("centerSheet.closeRequested")
                : t("centerSheet.centerClosed")
        );
    };

    const handleIncorrecto = () => {
        ejecutarAccionObra(
            "/api/Obra/MarkIncorrectObra",
            t("centerSheet.confirmMarkIncorrect", { code: centro?.codEspacio }),
            t("centerSheet.markedIncorrect")
        );
    };

    const handleQuitarIncorrecto = () => {
        ejecutarAccionObra(
            "/api/Obra/ClearIncorrectObra",
            t("centerSheet.confirmClearIncorrect", { code: centro?.codEspacio }),
            t("centerSheet.incorrectCleared")
        );
    };

    const handleDescargarPlantilla = async () => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/ObraDocumento/GetPlantillaObra`,
                { responseType: "blob" }
            );

            if (response.status !== 200) {
                throw new Error(t("centerSheet.errorDownloadingTheTemplate"));
            }

            const cd = response.headers["content-disposition"] || "";
            let fileName = `Plantilla_Obra_${id}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
            const normalMatch = cd.match(/filename="?([^"]+)"?/i);
            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }

            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            notificationRef.current?.addNotification(
                t("centerSheet.templateDownloadedSuccessfully"),
                "success"
            );
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                t("centerSheet.errorDownloadingTheTemplatePleaseTryAgain"),
                "error"
            );
        } finally {
            setExportando(false);
        }
    };

    const handleEditarCentro = () => {
        if (!centro) return;
        setFormEdicionCentro({
            codEspacio: centro.codEspacio || "",
            nombre: centro.nombre || "",
            direccion: centro.direccion || "",
            localidad: centro.localidad || "",
            codIntegracion: centro.codIntegracion || "",
            promotor: centro.promotor || "",
            observaciones: centro.observaciones || "",
            responsable: centro.responsable || "",
            mailResponsable: centro.mailResponsable || "",
        });
        setModalEdicionAbierto(true);
    };

    const handleCambioEdicionCentro = (campo, valor) => {
        setFormEdicionCentro((prev) => ({ ...prev, [campo]: valor }));
    };

    const handleGuardarEdicionCentro = async () => {
        if (!centro) return;
        try {
            const api = getApiInstance();
            const payload = {
                ...centro,
                ...formEdicionCentro,
            };
            delete payload.ProductorObj;
            delete payload.residuos;
            await api.put(`/api/Obra/UpdateObra`, payload);
            notificationRef.current?.addNotification(
                t("centerSheet.centerUpdatedSuccessfully"),
                "success"
            );
            setModalEdicionAbierto(false);
            setRefreshKey((k) => k + 1);
        } catch (err) {
            console.error(err);
            notificationRef.current?.addNotification(
                t("centerSheet.errorUpdatingTheCenterPleaseTryAgain"),
                "error"
            );
        }
    };

    const renderHeaderActions = () => {
        if (!centro) return null;

        const estado = Number(centro.codEstado);
        const esCerrado = estado === 2;
        const esIncorrecto = estado === 7;
        const esSinDatos = estado === 0 || estado === 1;
        const esSolicitudCierre = estado === 6;
        const esAdmin = [1, 2].includes(Number(user.role));
        const esIntermediario = Number(user.role) === 4;
        const puedeCerrarDirecto = puedeGestionarCentro && !esIntermediario;
        const puedeSolicitarCierre =
            puedeGestionarCentro && esIntermediario;
        /* El legacy oculta "Exportar Obra" de la ficha al Intermediario
           (Details.cshtml:100); desde el listado sí puede exportar. */
        const puedeExportarFicha = puedeGestionarCentro && !esIntermediario;

        const acciones = [];

        if (esCerrado) {
            if (puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? t("centerSheet.generating") : t("centerSheet.exportWork")}
                    </Button>
                );
            }
            if (puedeGestionarCentro && esAdmin) {
                acciones.push(
                    <Button
                        key="abrir"
                        variant="filled"
                        startIcon={<IoLockOpenOutline />}
                        onClick={handleAbrirCentro}
                    >
                        {t("centerSheet.openCenter")}
                    </Button>
                );
            }
        } else if (esIncorrecto) {
            if (puedeCerrarDirecto) {
                acciones.push(
                    <Button
                        key="quitar"
                        variant="outlined"
                        startIcon={<IoReloadOutline />}
                        onClick={handleQuitarIncorrecto}
                    >
                        {t("centerSheet.clearIncorrect")}
                    </Button>
                );
            }
            if (puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? t("centerSheet.generating") : t("centerSheet.exportWork")}
                    </Button>
                );
            }
        } else {
            if (puedeCerrarDirecto) {
                acciones.push(
                    <Button
                        key="plantilla"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleDescargarPlantilla}
                    >
                        {t("centerSheet.downloadTemplate")}
                    </Button>,
                    <Button
                        key="cerrar"
                        variant="outlined"
                        startIcon={<IoStopOutline />}
                        onClick={handleCerrarCentro}
                    >
                        {t("centerSheet.closeCenter")}
                    </Button>,
                    <Button
                        key="editar"
                        variant="outlined"
                        startIcon={<FiEdit />}
                        onClick={handleEditarCentro}
                    >
                        {t("centerSheet.editCenter")}
                    </Button>,
                    <Button
                        key="incorrecto"
                        variant="outlined"
                        className="ficha-centro__btn--danger"
                        startIcon={<IoClose />}
                        onClick={handleIncorrecto}
                    >
                        {t("centerSheet.incorrect")}
                    </Button>
                );
            }

            if (puedeSolicitarCierre && !esSolicitudCierre) {
                acciones.push(
                    <Button
                        key="solicitarCierre"
                        variant="outlined"
                        startIcon={<IoStopOutline />}
                        onClick={handleCerrarCentro}
                    >
                        {t("centerSheet.requestClosure")}
                    </Button>
                );
            }

            if (!esSinDatos && puedeExportarFicha) {
                acciones.push(
                    <Button
                        key="exportar"
                        variant="outlined"
                        startIcon={<FiDownload />}
                        onClick={handleExportarObra}
                        disabled={exportando}
                    >
                        {exportando ? t("centerSheet.generating") : t("centerSheet.exportWork")}
                    </Button>
                );
            }
        }

        return acciones;
    };

    return (
        <DetalleLayout
            title={
                centro
                    ? t("titles.centerHeader", {
                          code: centro.codEspacio,
                          address: centro.direccion,
                          city: centro.localidad,
                      })
                    : t("common.loading")
            }
            backLink="/centros-productores"
            backText={t("backTo.centers")}
            loading={loading}
            error={error}
            notFound={!centro && !loading && !error}
            className="ficha-centro"
            headerActions={renderHeaderActions()}
        >
            {isMabe && (
                <div className="ficha-centro__actions">
                    <Button
                        variant="filled"
                        onClick={handleBitacora}
                        className="ficha-centro__manifiesto-btn"
                    >
                        {t("centerSheet.getLog")}
                    </Button>
                </div>
            )}

            {!loading && !error && centro && (
                <div className="ficha-centro__content">
                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>{t("centerSheet.centerData")}</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />
                        <div className="ficha-centro__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>{t("centerSheet.waste")}</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsResiduos}
                            activeTab={activeTabResiduos}
                            onTabChange={setActiveTabResiduos}
                        />
                        <div className="ficha-centro__section-content">
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "cantidades"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                {renderCantidadesGeneradas()}
                            </div>
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "agrupados"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                {/* TABLE AGRUPADOS */}
                                <Table
                                    columns={[
                                        {
                                            key: "residuo",
                                            label: t("centerSheet.waste2"),
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "peso",
                                            label: t("centerSheet.weight"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) =>
                                                formatPesoResiduo(
                                                    row.peso,
                                                    row.codTipoResiduo
                                                ),
                                        },
                                        {
                                            key: "valorizacion",
                                            label: t("centerSheet.valorization"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) => {
                                                /* "Sin datos" solo si ninguna línea
                                                   trae tratamiento final con %. Todo a
                                                   eliminación es un dato: 0%. El % va
                                                   sobre el peso con dato, como el
                                                   informe y la app antigua. */
                                                if (
                                                    !(
                                                        row.pesoConDatoValorizacion >
                                                        0
                                                    )
                                                ) {
                                                    return (
                                                        <span>{t("centerSheet.noData")}</span>
                                                    );
                                                }

                                                const porcentajeAgrupado =
                                                    ((row.valorizacion || 0) /
                                                        row.pesoConDatoValorizacion) *
                                                    100;

                                                const peso =
                                                    pesoEnUnidadResiduo(
                                                        row.pesoConDatoValorizacion,
                                                        row.codTipoResiduo
                                                    );
                                                const valorizado =
                                                    pesoEnUnidadResiduo(
                                                        row.valorizacion,
                                                        row.codTipoResiduo
                                                    );

                                                return (
                                                    <div className="valorizacion-container">
                                                        <span htmlFor="valorizacion">
                                                            {valorizado.toFixed(
                                                                3
                                                            )}{" "}
                                                            / {peso}{" "}
                                                            {unidadResiduo(
                                                                row.codTipoResiduo
                                                            )}
                                                        </span>
                                                        <div className="valorizacion-barra">
                                                            <progress
                                                                id="valorizacion"
                                                                value={
                                                                    valorizado
                                                                }
                                                                max={peso}
                                                                className="progress-bar"
                                                                style={obtenerEstiloDeProgreso(
                                                                    valorizado,
                                                                    peso
                                                                )}
                                                            ></progress>
                                                            <span className="valorizacion-porcentaje">
                                                                {porcentajeAgrupado.toFixed(
                                                                    1
                                                                )}
                                                                %
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "costeTotal",
                                            label: t("centerSheet.totalCost"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) => (
                                                <span
                                                    className={`coste ${
                                                        row.costeTotal < 0
                                                            ? "coste--negative"
                                                            : "coste--positive"
                                                    }`}
                                                >
                                                    {formateaCoste.format(
                                                        row.costeTotal ?? 0
                                                    )}
                                                </span>
                                            ),
                                        },
                                    ]}
                                    dataTable={residuosObraGroupped}
                                    searchTerms={["residuo", "peso"]}
                                    noResultsMessage={t("centerSheet.noGroupedWasteAvailable")}
                                    rowsPerPage={10}
                                    keyProp="codResiduo"
                                    sortProp="residuo"
                                    showCheckboxes={false}
                                    elementName={t("elements.wasteLower")}
                                    stateProp="estado"
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    loading={false}
                                    excludeFields={["residuo", "codTipoResiduo", "codResiduo", "valorizacion", "pesoConDatoValorizacion", "pesoSinDatoValorizacion"]}
                                />
                            </div>
                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "generados"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                <Table
                                    columns={[
                                        {
                                            key: [
                                                "residuo/codigo",
                                                "residuo/nombre",
                                                "observacionesGestionFinal",
                                            ],
                                            label: t("centerSheet.waste2"),
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "fechaTransporte",
                                            label: t("centerSheet.pickupDate"),
                                            visible: true,
                                            sortable: true,
                                            type: "date",
                                        },
                                        {
                                            key: "peso",
                                            label: t("centerSheet.weight"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) =>
                                                formatPesoResiduo(
                                                    row.peso,
                                                    row.codTipoResiduo
                                                ),
                                        },
                                        {
                                            key: "valorizacion",
                                            label: t("centerSheet.valorization"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) => {
                                                /* La valorización y su % los calcula
                                                   el backend según el tratamiento
                                                   final (0 si es de eliminación);
                                                   Porcentaje2 solo no basta. */
                                                if (row.valorizacion == null) {
                                                    return (
                                                        <span>{t("centerSheet.noData")}</span>
                                                    );
                                                }

                                                /* El backend manda el peso en kg;
                                                   se muestra en la unidad del tipo
                                                   de residuo, como la app antigua. */
                                                const peso =
                                                    pesoEnUnidadResiduo(
                                                        row.peso,
                                                        row.codTipoResiduo
                                                    );
                                                const valorizado =
                                                    pesoEnUnidadResiduo(
                                                        row.valorizacion,
                                                        row.codTipoResiduo
                                                    );

                                                return (
                                                    <div className="valorizacion-container">
                                                        <span htmlFor="valorizacion">
                                                            {valorizado.toFixed(
                                                                3
                                                            )}{" "}
                                                            / {peso}{" "}
                                                            {unidadResiduo(
                                                                row.codTipoResiduo
                                                            )}
                                                        </span>
                                                        <div className="valorizacion-barra">
                                                            <progress
                                                                id="valorizacion"
                                                                value={
                                                                    valorizado
                                                                }
                                                                max={peso}
                                                                className="progress-bar"
                                                                style={obtenerEstiloDeProgreso(
                                                                    valorizado,
                                                                    peso
                                                                )}
                                                            ></progress>
                                                            <span className="valorizacion-porcentaje">
                                                                {(
                                                                    Number(
                                                                        row.porcentajeValorizacion
                                                                    ) || 0
                                                                ).toFixed(1)}
                                                                %
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "costeTotal",
                                            label: t("centerSheet.finalCost"),
                                            visible: true,
                                            sortable: true,
                                            render: (row) => (
                                                <span
                                                    className={`coste ${
                                                        row.costeTotal < 0
                                                            ? "coste--negative"
                                                            : "coste--positive"
                                                    }`}
                                                >
                                                    {formateaCoste.format(
                                                        row.costeTotal ?? 0
                                                    )}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: "codEstado",
                                            label: t("centerSheet.validation"),
                                            visible: true,
                                            sortable: true,
                                            titleProp: "lastValidationNote",
                                        },
                                    ]}
                                    dataTable={residuosGenerados}
                                    noResultsMessage={t("centerSheet.noGeneratedWasteAvailable")}
                                    rowsPerPage={10}
                                    showCheckboxes={false}
                                    /* Sobre un centro cerrado o incorrecto no se
                                       añaden residuos. La tabla solo mira el acceso
                                       y el perfil, no el estado del centro. */
                                    hideAddButton={!puedeEditarResiduos}
                                    codTipoValidacion={2}
                                    actions={[
                                        ...(puedeEditarResiduos
                                            ? ["Editar"]
                                            : []),
                                        ...(puedeValidar
                                            ? [
                                                  {
                                                      label: t("centerSheet.validate"),
                                                      actionType: "validate",
                                                      icon: (
                                                          <CiCircleCheck
                                                              size={16}
                                                          />
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        ...(puedeVerValidaciones
                                            ? [
                                                  {
                                                      label: t("centerSheet.viewValidations"),
                                                      action: abrirHistoricoValidaciones(
                                                          2
                                                      ),
                                                      icon: (
                                                          <CiClock1 size={16} />
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        /* "Generar manifiesto": el legacy lo
                                           mostraba solo en la zona 2 (México);
                                           aquí el equivalente es el flag isMabe. */
                                        ...(isMabe ? ["Exportar"] : []),
                                        ...(puedeEditarResiduos
                                            ? ["Eliminar"]
                                            : []),
                                    ]}
                                    onDataUpdate={(newData) => {
                                        // Optimista para que la fila cambie ya, y
                                        // recarga para traer los datos calculados.
                                        setResiduosGenerados(newData);
                                        recargarResiduos();
                                    }}
                                    elementName={t("elements.waste")}
                                    sortProp="codResiduoObra"
                                    stateProp="codEstado"
                                    states={[
                                        {
                                            label: t("centerSheet.noData"),
                                            type: "info",
                                            Icon: CiClock1,
                                        },
                                        {
                                            label: t("centerSheet.pendingValidation"),
                                            type: "info",
                                            Icon: CiClock1,
                                        },
                                        {
                                            label: t("centerSheet.validationWithIncidents"),
                                            type: "danger",
                                            Icon: CgDanger,
                                        },
                                        {
                                            label: t("centerSheet.validated"),
                                            type: "success",
                                            Icon: CiCircleCheck,
                                        },
                                    ]}
                                    keyProp="codResiduoObra"
                                    formElements={[
                                        {
                                            title: t("centerSheet.waste2"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codResiduo",
                                                    type: "select",
                                                    label: t("centerSheet.wasteType"),
                                                    placeholder:
                                                        t("centerSheet.selectTheWasteType"),
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: residuos,
                                                },
                                                {
                                                    name: "codProyecto",
                                                    type: "select",
                                                    label: t("centerSheet.project"),
                                                    placeholder:
                                                        t("centerSheet.selectProject"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: proyectos,
                                                },

                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "clasificacion",
                                                              type: "select",
                                                              label: t("centerSheet.classification"),
                                                              placeholder:
                                                                  t("centerSheet.selectClassification"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: "RR/RR",
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: "RNV",
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "estadoFisico",
                                                              type: "select",
                                                              label: t("centerSheet.physicalState"),
                                                              placeholder:
                                                                  t("centerSheet.selectPhysicalState"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: t("centerSheet.solid"),
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: t("centerSheet.liquid"),
                                                                  },
                                                                  {
                                                                      value: "3",
                                                                      label: t("centerSheet.gaseous"),
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "formaManejo",
                                                              type: "select",
                                                              label: t("centerSheet.handlingMethod"),
                                                              placeholder:
                                                                  t("centerSheet.selectHandlingMethod"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options: [
                                                                  {
                                                                      value: "1",
                                                                      label: t("centerSheet.ccControlledConfinement"),
                                                                  },
                                                                  {
                                                                      value: "2",
                                                                      label: t("centerSheet.cersSpecialCellsInSanitaryLandfills"),
                                                                  },
                                                              ],
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "codObra",
                                                    type: "text",
                                                    label: "CodObra",
                                                    placeholder:
                                                        "Inserte codObra",
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: false,
                                                    visible: false,
                                                    defaultValue:
                                                        centro.codObra,
                                                },
                                                {
                                                    name: "codZona",
                                                    type: "text",
                                                    label: "CodZona",
                                                    placeholder:
                                                        "Inserte codZona",
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: false,
                                                    visible: false,
                                                    defaultValue:
                                                        selectedZone.value,
                                                },
                                            ],
                                        },
                                        {
                                            title: t("centerSheet.transport"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    /* Un residuo tiene UN transportista:
                                                       ResiduosObra.CodTransportista es una
                                                       sola columna y ResiduosObraDTO una sola
                                                       propiedad. El multiselect en plural que
                                                       habia aqui no existe en el DTO, asi que
                                                       ni se guardaba ni se recuperaba. */
                                                    name: "codTransportista",
                                                    type: "select",

                                                    label: t("centerSheet.carrier"),
                                                    placeholder:
                                                        t("centerSheet.selectTheCarrier"),
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: transportistas,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaTransporte",
                                                    type: "date",
                                                    label: t("centerSheet.transportDate"),
                                                    placeholder:
                                                        t("centerSheet.selectTheTransportDate"),
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "codVehiculo",
                                                    type: "select",
                                                    label: t("centerSheet.vehicleType"),
                                                    placeholder:
                                                        t("centerSheet.selectTheVehicleType"),
                                                    required: isMabe
                                                        ? true
                                                        : false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: vehiculos,
                                                },
                                                {
                                                    name: "tipoCombustible",
                                                    type: "select",
                                                    label: t("centerSheet.fuelType"),
                                                    placeholder:
                                                        t("centerSheet.selectTheFuelType"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: combustibles,
                                                    associatedFormElement:
                                                        "codVehiculo",
                                                },
                                                {
                                                    name: "numKm",
                                                    type: "number",
                                                    label: t("centerSheet.numberOfKm"),
                                                    placeholder:
                                                        t("centerSheet.enterTheNumberOfKilometers"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "numViajes",
                                                    type: "number",
                                                    label: t("centerSheet.numberOfTrips"),
                                                    placeholder:
                                                        t("centerSheet.enterTheNumberOfTrips"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "nombreTransportista",
                                                              type: "text",
                                                              label: t("centerSheet.carrierName"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheCarrierName"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numLicenciaTransportista",
                                                              type: "text",
                                                              label: t("centerSheet.licenseNumber"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheLicenseNumber"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipoLicenciaTransportista",
                                                              type: "text",
                                                              label: t("centerSheet.licenseType"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheLicenseType"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numPlacaTransportista",
                                                              type: "text",
                                                              label: t("centerSheet.licensePlateNumber"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheLicensePlateNumber"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "ficheroTransportista",
                                                    type: "document",
                                                    label: t("centerSheet.transportDocument"),
                                                    placeholder:
                                                        t("centerSheet.selectTheTransportDocument"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: t("centerSheet.intermediateManagement"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codGestorIntermedio",
                                                    type: "select",

                                                    label: t("centerSheet.intermediateManager"),
                                                    placeholder:
                                                        t("centerSheet.selectTheIntermediateManager"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: gestores,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaGestionIntermedia",
                                                    type: "date",
                                                    label: t("centerSheet.intermediateManagementDate"),
                                                    placeholder:
                                                        t("centerSheet.selectTheIntermediateManagementDate"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "tipoTratamiento",
                                                    type: "select",
                                                    label: t("centerSheet.treatmentType"),
                                                    placeholder:
                                                        t("centerSheet.selectTheTreatmentType"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: [
                                                        {
                                                            value: "Eliminación",
                                                            label: t("centerSheet.elimination"),
                                                        },
                                                        {
                                                            value: "Valorización",
                                                            label: t("centerSheet.valorization"),
                                                        },
                                                    ],
                                                },
                                                {
                                                    name: "codTipoTratamiento",
                                                    type: "select",
                                                    label: t("centerSheet.treatmentTypeDescription"),
                                                    placeholder:
                                                        t("centerSheet.enterTheTreatmentTypeDescription"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: tiposTratamiento,
                                                    associatedFormElement:
                                                        "tipoTratamiento",
                                                },
                                                {
                                                    name: "porcentaje",
                                                    type: "number",
                                                    label: t("centerSheet.percentage"),
                                                    placeholder:
                                                        t("centerSheet.enterThePercentage"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: t("centerSheet.finalManagement"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "codGestorFinal",
                                                    type: "select",
                                                    label: t("centerSheet.finalManager"),
                                                    placeholder:
                                                        t("centerSheet.selectTheFinalManager"),
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: gestores,
                                                    associatedFormElement:
                                                        "codResiduo",
                                                },
                                                {
                                                    name: "fechaTratamiento",
                                                    type: "date",
                                                    label: t("centerSheet.finalManagementDate"),
                                                    placeholder:
                                                        t("centerSheet.selectTheFinalManagementDate"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "tipoTratamiento2",
                                                    type: "select",
                                                    label: t("centerSheet.treatmentType"),
                                                    placeholder:
                                                        t("centerSheet.selectTheTreatmentType"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: [
                                                        {
                                                            value: "Eliminación",
                                                            label: t("centerSheet.elimination"),
                                                        },
                                                        {
                                                            value: "Valorización",
                                                            label: t("centerSheet.valorization"),
                                                        },
                                                    ],
                                                },
                                                {
                                                    name: "codTipoTratamiento2",
                                                    type: "select",
                                                    label: t("centerSheet.treatmentTypeDescription"),
                                                    placeholder:
                                                        t("centerSheet.enterTheTreatmentTypeDescription"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                    options: tiposTratamiento,
                                                    associatedFormElement:
                                                        "tipoTratamiento2",
                                                },
                                                {
                                                    name: "porcentaje2",
                                                    type: "number",
                                                    label: t("centerSheet.percentage"),
                                                    placeholder:
                                                        t("centerSheet.enterThePercentage"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipoVehiculoGestorFinal",
                                                              type: "select",
                                                              label: t("centerSheet.vehicleType"),
                                                              placeholder:
                                                                  t("centerSheet.selectTheVehicleType"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                              options:
                                                                  vehiculos,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "numPlacaGestorFinal",
                                                              type: "text",
                                                              label: t("centerSheet.licensePlateNumber"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheLicensePlateNumber"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "nombreGestorFinal",
                                                              type: "text",
                                                              label: t("centerSheet.finalManagerName"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheFinalManagerName"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "cargoGestorFinal",
                                                              type: "text",
                                                              label: t("centerSheet.finalManagerPosition"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheFinalManagerPosition"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    name: "observacionesGestionFinal",
                                                    type: "textarea",
                                                    label: t("centerSheet.finalManagementObservations"),
                                                    placeholder:
                                                        t("centerSheet.enterTheFinalManagementObservations"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "ficheroGestorFinal",
                                                    type: "document",
                                                    label: t("centerSheet.finalManagerDocument"),
                                                    placeholder:
                                                        t("centerSheet.selectTheFinalManagerDocument"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                        {
                                            title: t("centerSheet.generatedQuantities"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "peso",
                                                    type: "number",
                                                    label: t("centerSheet.weightKg"),
                                                    labelDinamica:
                                                        etiquetaPesoResiduo,
                                                    placeholder:
                                                        t("centerSheet.enterTheWeight"),
                                                    required: true,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "volumen",
                                                    type: "number",
                                                    label: t("centerSheet.volumeM3"),
                                                    placeholder:
                                                        t("centerSheet.enterTheVolume"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "capacidad",
                                                              type: "text",
                                                              label: t("centerSheet.capacity"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheCapacity"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                                ...(isMabe
                                                    ? [
                                                          {
                                                              name: "tipo",
                                                              type: "text",
                                                              label: t("centerSheet.type"),
                                                              placeholder:
                                                                  t("centerSheet.enterTheType"),
                                                              required: true,
                                                              disabled: false,
                                                              labelVisible: true,
                                                              visible: true,
                                                          },
                                                      ]
                                                    : []),
                                            ],
                                        },
                                        {
                                            title: t("centerSheet.cost"),
                                            titleVisible: true,
                                            elements: [
                                                {
                                                    name: "costeTransporte",
                                                    type: "number",
                                                    label: t("centerSheet.transport"),
                                                    placeholder:
                                                        t("centerSheet.enterTheTransportCost"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeGestion",
                                                    type: "number",
                                                    label: t("centerSheet.management"),
                                                    placeholder:
                                                        t("centerSheet.enterTheManagementCost"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeColocacion",
                                                    type: "number",
                                                    label: t("centerSheet.containerPlacement"),
                                                    placeholder:
                                                        t("centerSheet.enterTheContainerPlacementCost"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeAlquiler",
                                                    type: "number",
                                                    label: t("centerSheet.containerRental"),
                                                    placeholder:
                                                        t("centerSheet.enterTheContainerRentalCost"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    name: "costeDocumento",
                                                    type: "number",
                                                    label: t("centerSheet.documentGeneration"),
                                                    placeholder:
                                                        t("centerSheet.enterTheDocumentGenerationCost"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                                {
                                                    /* Es un ingreso por la venta de
                                                       chatarra: se introduce en
                                                       negativo y resta del coste
                                                       total, como en la aplicación
                                                       antigua (el input tenía
                                                       max="0"). */
                                                    name: "abonoChatarra",
                                                    type: "number",
                                                    label: t("centerSheet.scrapCredit"),
                                                    placeholder:
                                                        t("centerSheet.enterTheCreditAsANegativeValue"),
                                                    required: false,
                                                    disabled: false,
                                                    labelVisible: true,
                                                    visible: true,
                                                },
                                            ],
                                        },
                                    ]}
                                    stateChangeOptions={[
                                        {
                                            value: "1",
                                            label: t("centerSheet.pendingValidation"),
                                        },
                                        {
                                            value: "2",
                                            label: t("centerSheet.validationWithIncidents"),
                                        },
                                        { value: "3", label: t("centerSheet.validated") },
                                    ]}
                                    /* Sin permiso para validar no se pasa la URL: así la tabla no
                                       ofrece el cambio de estado ni el botón de validar en bloque. */
                                    stateChangeUrl={
                                        puedeValidar
                                            ? "/api/Validaciones/SetValidacion"
                                            : undefined
                                    }
                                    addApiUrl="/api/ResiduosObra/SetResiduoObra"
                                    editApiUrl="/api/ResiduosObra/UpdateResiduoObra"
                                    deleteApiUrl="/api/ResiduosObra/DeleteResiduoObra"
                                    /* Siempre multipart y con el peso en kg: el
                                       formulario lo pide en tn para los no
                                       peligrosos. */
                                    payloadMapper={residuoFormAPayload}
                                    rowToFormMapper={residuoFilaAFormulario}
                                    validateForm={validaResiduoForm}
                                    onRowClick={(row) =>
                                        setResiduoDetalle(row)
                                    }
                                    searchTerms={[
                                        "residuo/nombre",
                                        "fechaRecogida",
                                        "peso",
                                        "costeFinal",
                                    ]}
                                    loading={loading}
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    /* El intermediario añade, edita y elimina
                                       residuos de su obra, como en la antigua. */
                                    intermediarioPuedeEscribir
                                    exportApiUrl="/api/ResiduosObra/obtener-informe1-mabe"
                                    exportActionLabel="Generar manifiesto"
                                    extraPropExportKey="codObra"
                                    extraPropExportText="codObra"
                                    excludeFields={[
                                        "observacionesGestionFinal", 
                                        "residuo/nombre", 
                                        "fechaRecogida", 
                                        "codResiduoObra", 
                                        "nombreObra", 
                                        "codTipoObra", 
                                        "codProductor", 
                                        "codPais", 
                                        "codResiduo", 
                                        "codProyecto", 
                                        "codEstadoFisico", 
                                        "codPeligrosidad", 
                                        "codTipoResiduo", 
                                        "codIdentificacion", 
                                        "codTipoGestion", 
                                        "codTransportista", 
                                        "documentoTransporte", 
                                        "fechaSubida", 
                                        "fechaGestionIntermedia", 
                                        "unidadPeso", 
                                        "codVehiculo", 
                                        "codCombustible", 
                                        "codGestorFinal", 
                                        "codGestorIntermedio", 
                                        "codTipoTratamiento", 
                                        "codTipoTratamiento2", 
                                        "codPaisGestorFinal", 
                                        "codPaisGestorIntermedio", 
                                        "codPaisTransportista", 
                                        "codProvinciaGestorFinal", 
                                        "codProvinciaGestorIntermedio", 
                                        "codProvinciaTransportista", 
                                        "documentoFinal", 
                                        "documento", 
                                        "alquiler", 
                                        "colocacion", 
                                        "autorizacionGestorFinal", 
                                        "fechaFinObra", 
                                        "ficheroTransportista", 
                                        "ficheroGestorFinal", 
                                        "lastValidationNote", 
                                        "codEstado", 
                                        "autorizacionTransportista", 
                                        "eliminacion", 
                                        "valorizacion", 
                                        "pesoConUnidad", 
                                        "porcentaje", 
                                        "porcentaje2", 
                                        "numDocumentoFinal", 
                                        "numDocumentoTransporte", 
                                        "costeFinal", 
                                        "numKm", 
                                        "numViajes"]}
                                />
                            </div>

                            <div
                                style={{
                                    display:
                                        activeTabResiduos === "tarifas"
                                            ? "block"
                                            : "none",
                                }}
                            >
                                <Table
                                    columns={[
                                        {
                                            key: "gestor",
                                            label: t("centerSheet.manager"),
                                            visible: true,
                                            sortable: true,
                                        },
                                        {
                                            key: "tarifa",
                                            label: t("centerSheet.rate"),
                                            visible: true,
                                            sortable: false,
                                        },
                                    ]}
                                    keyProp="codTarifa"
                                    dataTable={tarifas}
                                    noResultsMessage={t("centerSheet.noRatesAvailable")}
                                    rowsPerPage={10}
                                    showCheckboxes={false}
                                    elementName={t("elements.rate")}
                                    searchTerms={["gestor", "tarifa"]}
                                    showAdvancedFilters={false}
                                    userCodAcceso={Number(user.codAcceso)}
                                    userCodPerfil={Number(user.role)}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header">
                            <h2>{t("centerSheet.documentation")}</h2>
                        </div>
                        <TabsNavigation
                            tabs={tabsDocumentacion}
                            activeTab={activeTabDocumentacion}
                            onTabChange={setActiveTabDocumentacion}
                        />
                        <div className="ficha-centro__section-content">
                            {renderDocumentacionContent()}
                        </div>
                    </div>

                    <div className="ficha-centro__section">
                        <div className="ficha-centro__section-header ficha-centro__section-header--with-action">
                            <h2>{t("centerSheet.consumption")}</h2>
                            {puedeGestionarCentro && (
                                <Button
                                    variant="filled"
                                    startIcon={<IoMdAdd />}
                                    onClick={() =>
                                        consumoTableRef.current?.openAddModal()
                                    }
                                >
                                    {t("centerSheet.addConsumption")}
                                </Button>
                            )}
                        </div>
                        <div className="ficha-centro__section-content">
                            {renderConsumo()}
                        </div>
                    </div>
                    <Modal
                        isOpen={isModalBitacoraOpen}
                        onClose={() => {
                            setIsModalBitacoraOpen(false);
                            setSelectedMonthBitacora(null);
                        }}
                        className="modal--dropdown-visible"
                        title={t("centerSheet.selectTheMonthOfTheLogTo")}
                        actions={[
                            <Button
                                onClick={() => handleBitacoraResiduo()}
                                startIcon={<FiFileText />}
                            >
                                {t("centerSheet.generateReport")}
                            </Button>,
                        ]}
                    >
                        <InputDropdown
                            label={t("centerSheet.logMonth")}
                            placeholder={t("centerSheet.selectTheMonth")}
                            options={[
                                { value: 1, label: t("calendar.months.january") },
                                { value: 2, label: t("calendar.months.february") },
                                { value: 3, label: t("calendar.months.march") },
                                { value: 4, label: t("calendar.months.april") },
                                { value: 5, label: t("calendar.months.may") },
                                { value: 6, label: t("calendar.months.june") },
                                { value: 7, label: t("calendar.months.july") },
                                { value: 8, label: t("calendar.months.august") },
                                { value: 9, label: t("calendar.months.september") },
                                { value: 10, label: t("calendar.months.october") },
                                { value: 11, label: t("calendar.months.november") },
                                { value: 12, label: t("calendar.months.december") },
                            ]}
                            defaultValue={selectedMonthBitacora}
                            onChange={(value) =>
                                setSelectedMonthBitacora(value)
                            }
                            multiple={false}
                        />
                    </Modal>
                    <Modal
                        isOpen={modalEdicionAbierto}
                        onClose={() => setModalEdicionAbierto(false)}
                        title={t("centerSheet.editCenter")}
                        actions={[
                            <Button
                                key="guardar"
                                onClick={() => handleGuardarEdicionCentro()}
                                startIcon={<FiEdit />}
                            >
                                {t("centerSheet.saveChanges")}
                            </Button>,
                        ]}
                    >
                        <div className="ficha-centro__edit-form">
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.spaceCode")}</label>
                                <input
                                    value={formEdicionCentro.codEspacio || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "codEspacio",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.name")}</label>
                                <input
                                    value={formEdicionCentro.nombre || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "nombre",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.address")}</label>
                                <input
                                    value={formEdicionCentro.direccion || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "direccion",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.locality")}</label>
                                <input
                                    value={formEdicionCentro.localidad || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "localidad",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.integrationCode")}</label>
                                <input
                                    value={
                                        formEdicionCentro.codIntegracion || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "codIntegracion",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.promoter")}</label>
                                <input
                                    value={formEdicionCentro.promotor || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "promotor",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.responsible")}</label>
                                <input
                                    value={formEdicionCentro.responsable || ""}
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "responsable",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.responsibleEmail")}</label>
                                <input
                                    value={
                                        formEdicionCentro.mailResponsable || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "mailResponsable",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="ficha-centro__field">
                                <label>{t("centerSheet.observations")}</label>
                                <textarea
                                    rows={3}
                                    value={
                                        formEdicionCentro.observaciones || ""
                                    }
                                    onChange={(e) =>
                                        handleCambioEdicionCentro(
                                            "observaciones",
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </Modal>

                    {/* GetObra trae `validaciones` en el payload, pero se pide al
                        abrir el modal: si el usuario acaba de validar, la lista de
                        la carga inicial estaría desactualizada. */}
                    <ResiduoDetalleModal
                        isOpen={residuoDetalle != null}
                        onClose={() => setResiduoDetalle(null)}
                        residuo={residuoDetalle}
                        catalogos={{
                            proyectos,
                            vehiculos,
                            combustibles,
                            tiposTratamiento,
                        }}
                        formateaCoste={formateaCoste}
                    />

                    <ValidacionesHistorico
                        isOpen={isHistoricoOpen}
                        onClose={() => setIsHistoricoOpen(false)}
                        codTipoValidacion={
                            historicoValidacion?.codTipoValidacion
                        }
                        codClave={historicoValidacion?.codClave}
                    />

                    <Notification ref={notificationRef} />
                </div>
            )}
        </DetalleLayout>
    );
}
